import React from 'react';
import { Icon } from '@iconify-icon/react';
import { Button } from '@mantine/core';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

const highlights = [
  'Connect docs, wikis, Drive & site pages',
  'Cited answers your whole team can trust',
  'Private by design — never used for training',
  'Live in days, not a multi-year AI program',
];

const CompanyBrainCTA: React.FC = () => (
  <section aria-label="Start building your company brain" className="bg-white pb-20 sm:pb-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b8862e]">
            Get started
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Build your Company Brain{' '}
            <span className="font-display font-medium italic text-[#004790]">today.</span>
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-gray-500">
            Upload what you already have. Give every teammate a single place to ask. Keep the
            answers grounded in your sources &mdash; and keep the corpus confidential.
          </p>
          <ul className="mt-8 space-y-3.5">
            {highlights.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50">
                  <Icon icon="tabler:check" className="text-sm text-emerald-600" />
                </span>
                <span className="text-[15px] font-medium leading-relaxed text-gray-800">
                  {item}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs leading-relaxed text-gray-400">
            Learn more about our privacy posture on the{' '}
            <Link
              href={PageLink.privacyPage}
              className="underline underline-offset-2 hover:text-gray-600">
              privacy page
            </Link>
            , or read the full{' '}
            <Link
              href={PageLink.privacy}
              className="underline underline-offset-2 hover:text-gray-600">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div className="relative overflow-hidden rounded-[28px] bg-[#04182e] p-8 sm:p-12">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff07_1px,transparent_1px),linear-gradient(to_bottom,#ffffff07_1px,transparent_1px)] bg-[size:3rem_3rem]" />
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-600/40 blur-[80px]" />
          </div>
          <div className="relative">
            <Icon icon="tabler:brain" className="text-4xl text-[#d3a641]" />
            <h3 className="mt-5 font-display text-2xl font-medium italic leading-snug text-white sm:text-3xl">
              Connect the knowledge.
              <br />
              Unlock the company.
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-blue-200/70">
              Start a 14-day free trial with your own documents, or talk to us about rolling a
              Company Brain out across departments with SSO and access controls.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                component={Link}
                href={PageLink.login}
                className="hero-cta-btn rounded-xl bg-primary-600 px-8 text-base font-semibold shadow-lg shadow-blue-900/40">
                Start 14-Day Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href={PageLink.contactUs}
                classNames={{
                  root: 'rounded-xl border-white/25 px-8 text-base font-semibold text-white hover:bg-white/10',
                }}>
                Talk to us
              </Button>
            </div>
            <p className="mt-4 text-xs text-blue-200/50">
              No credit card required. Paid plan required after the trial.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default CompanyBrainCTA;
